import React from 'react';
import {View, ViewStyle} from 'react-native';

import Card from '/component/base/Card';
import Delta from '/component/base/Delta';
import {InlineFormattedNumber} from '/component/base/FormattedNumber';
import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import {Cards} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';
import testProperties from '/util/testing/testProperties';

import {styles} from './TeamsVolumeSummaryCard.style';
import {
  Competency,
  getTeamSummaryKeys,
  sdlmTransformer,
} from './TeamsVolumeSummaryCard.util';
interface TeamsVolumeSummaryCardProps {
  currentUserCompetency: Competency;
  metrics: Record<string, number | undefined>;
  style: ViewStyle;
}

type RenderItemProps = {
  label: string;
  value: number;
  testID?: string | null;
  extraSuffix?: React.ReactNode;
  style?: {
    base?: object;
    suffix?: object;
  };
  titleTestID?: string | null;
};

export const TeamsVolumeSummaryCard: React.FC<TeamsVolumeSummaryCardProps> = ({
  currentUserCompetency,
  metrics = {},
  style = {},
}) => {
  const {tt} = useTranslation('screen.coaches.card.volumesSummary');
  const {
    lastKey,
    lastLabel,
    newKey,
    newLabel,
    newTestID,
    newTitleTestID,
    projectedKey,
    projectedLabel,
    projectedTestID,
    projectedTitleTestID,
    totalKey,
    totalLabel,
    totalTestID,
    totalTitleTestID,
    projectedTotalKey,
    projectedTotalLabel,
    projectedTotalTestID,
    projectedTotalTitleTestID,
  } = getTeamSummaryKeys(currentUserCompetency);
  const {
    sdlmVolume = 0,
    sdlmPrimaryDelta = 0,
    sdlmSecondaryDelta = 0,
    sdlmGenVolume = 0,
    projectedGenVolume = 0,
    totalVolume = 0,
    projectedVolume = 0,
    currentVolume = 0,
  } = metrics;

  const renderListItem = ({
    label,
    value,
    testID,
    extraSuffix,
    style = {},
    titleTestID,
  }: RenderItemProps) => (
    <ListItem
      {...testProperties(Cards.cardListItem, true)}
      label={
        <Text
          emphasis="standard"
          allowFontScaling
          maxFontSizeMultiplier={2}
          testID={titleTestID}>
          {label}
        </Text>
      }
      readonly
      suffix={
        <>
          {extraSuffix}
          <InlineFormattedNumber testID={testID} value={value} truncate />
        </>
      }
      style={{
        base: {
          ...styles.listItem,
          ...(style.base || {}),
        },
        suffix: style.suffix || {},
      }}
    />
  );

  const showFqv = metrics?.sdlmVolume !== undefined;
  const showGen = metrics?.sdlmGenVolume !== undefined;

  return (
    <Card
      fullWidth
      testID={Cards.teamVolumesSummaryCard}
      style={{...styles.base, ...style}}>
      <View accessible accessibilityRole="header">
        <Text
          accessibilityRole="header"
          testID={Cards.teamVolumesSummaryCardTitle}
          variant="heading"
          allowFontScaling
          maxFontSizeMultiplier={2}
          style={styles.cardHeaderText}>
          {tt('heading', 'Team Volumes Summary')}
        </Text>
      </View>

      {renderListItem({
        label: tt(newKey, newLabel),
        value: currentVolume,
        testID: newTestID,
        titleTestID: newTitleTestID,
      })}

      {renderListItem({
        label: tt(projectedKey, projectedLabel),
        value: projectedVolume,
        testID: projectedTestID,
        titleTestID: projectedTitleTestID,
      })}

      {showFqv &&
        renderListItem({
          label: tt(lastKey, lastLabel),
          value: sdlmTransformer(sdlmVolume),
          testID: Cards.cardSameLastMonth1Value,
          extraSuffix: (
            <Delta
              testID={Cards.cardSameLastMonthDelta1Value}
              value={sdlmTransformer(sdlmPrimaryDelta)}
              style={{base: styles.delta}}
            />
          ),
          style: {suffix: styles.value},
          titleTestID: Cards.cardSameLastMonth1Title,
        })}

      <View style={styles.divider} />

      {renderListItem({
        label: tt(totalKey, totalLabel),
        value: totalVolume,
        testID: totalTestID,
        titleTestID: totalTitleTestID,
      })}

      {renderListItem({
        label: tt(projectedTotalKey, projectedTotalLabel),
        value: projectedGenVolume,
        testID: projectedTotalTestID,
        titleTestID: projectedTotalTitleTestID,
      })}

      {showGen &&
        renderListItem({
          label: tt(lastKey, lastLabel),
          value: sdlmTransformer(sdlmGenVolume),
          testID: Cards.cardSameLastMonth2Value,
          extraSuffix: (
            <Delta
              testID={Cards.cardSameLastMonthDelta2Value}
              value={sdlmTransformer(sdlmSecondaryDelta)}
              style={{base: styles.delta}}
            />
          ),
          style: {
            base: {...styles.listItem, ...styles.listItemLast},
            suffix: styles.value,
          },
          titleTestID: Cards.cardSameLastMonth2Title,
        })}
    </Card>
  );
};

export default TeamsVolumeSummaryCard;
